import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { LoadingButton } from '@mui/lab';
import { getPolyglot } from '../../../../i18n';
import StoerkIdAPI from '../../../../redux/stoerkID/StoerkIdAPI';
import { useAppDispatch, useAppSelector } from '../../../../redux/store.model';
import { WorkspaceAPI } from '../../../../redux/stoerkID/StoerkIdAPI.model';
import {
  LightTable,
  LightTableColumDef,
  LabelState,
} from '../../../../theme/components';
import { Link as LinkRouter } from 'react-router-dom';
import { Link } from '@mui/material';
import UserAPI from '../../../../redux/users/api/UserAPI';
import { getUserSelector } from '../../../../redux/users/api/User.selectors';
import useUsers from '../../../../redux/users/hooks/useUsers';
import useShowErrorMessage from '../../../../handlingErrors/useShowErrorMessage';
import LabelTitle from '../../../../theme/components/Forms/LabelTitle';

interface SubWorkspacesOverviewProps {
  workspace: WorkspaceAPI;
}

function SubWorkspacesOverview({ workspace }: SubWorkspacesOverviewProps) {
  const polyglot = getPolyglot();
  const dispatch = useAppDispatch();

  const { data: subWorkspaces, isLoading } =
    StoerkIdAPI.useGetSubWorkspacesQuery(workspace.id);

  const childIds = useMemo(
    () => (subWorkspaces ? Object.keys(subWorkspaces) : []),
    [subWorkspaces]
  );

  const hasChildren = childIds.length > 0;

  // Fetch workspace details for each child
  useEffect(() => {
    childIds.forEach((childId) => {
      dispatch(StoerkIdAPI.endpoints.getWorkspace.initiate(childId));
      dispatch(StoerkIdAPI.endpoints.getWorkspaceUsage.initiate(childId));
    });
  }, [dispatch, childIds]);

  const childData = useAppSelector((state) =>
    childIds.map((childId) => {
      const ws = StoerkIdAPI.endpoints.getWorkspace.select(childId)(state).data;
      const usage =
        StoerkIdAPI.endpoints.getWorkspaceUsage.select(childId)(state).data;
      const firstUser = ws?.users?.[0];
      return { childId, workspace: ws, usage, firstUser };
    })
  );

  // Fetch first user emails using memoized args
  const firstUserIds = useMemo(
    () =>
      childData.map(({ firstUser }) => firstUser).filter(Boolean) as string[],
    [childData]
  );

  const users = useUsers(firstUserIds);

  // Calculate quota allocation summary
  const quotaSummary = useMemo(() => {
    if (!subWorkspaces) return null;
    const allocations = Object.values(subWorkspaces);
    const allocatedDevices = allocations.reduce((s, a) => s + a.devices, 0);
    const allocatedUsers = allocations.reduce((s, a) => s + a.users, 0);
    return {
      totalDevices: workspace.quota.devices + allocatedDevices,
      totalUsers: workspace.quota.users + allocatedUsers,
      allocatedDevices,
      allocatedUsers,
      remainingDevices: workspace.quota.devices - allocatedDevices,
      remainingUsers: workspace.quota.users - allocatedUsers,
      childCount: allocations.length,
    };
  }, [subWorkspaces, workspace.quota]);

  // Edit quota dialog state
  const [editQuota, setEditQuota] = useState<{
    childId: string;
    users: number;
    devices: number;
  } | null>(null);

  const [updateQuota, { isLoading: isSavingQuota }] =
    StoerkIdAPI.useUpdateSubWorkspaceQuotaMutation();
  const showError = useShowErrorMessage();

  const handleSaveQuota = async () => {
    if (!editQuota) return;
    try {
      await updateQuota({
        workspaceId: workspace.id,
        subWorkspaceId: editQuota.childId,
        quota: { users: editQuota.users, devices: editQuota.devices },
      }).unwrap();
      setEditQuota(null);
    } catch (error) {
      showError(error);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasChildren) return null;

  return (
    <Box>
      <Typography variant="h2" mb={1}>
        {polyglot.t('stoerk_id.subworkspaces.title')}
      </Typography>

      {/* Quota summary */}
      {quotaSummary && (
        <Box display="flex" gap={4} mb={2} flexWrap="wrap">
          <Box>
            <LabelTitle>
              {polyglot.t('stoerk_id.subworkspaces.type_account')}
            </LabelTitle>
            <Typography variant="body2">
              {polyglot.t('stoerk_id.subworkspaces.type_organization')}
            </Typography>
          </Box>
          <Box>
            <LabelTitle>
              {polyglot.t('stoerk_id.subworkspaces.accounts_created')}
            </LabelTitle>
            <Typography variant="body2">{quotaSummary.childCount}</Typography>
          </Box>
          <Box>
            <LabelTitle>
              {polyglot.t('stoerk_id.subworkspaces.devices_allocated')}
            </LabelTitle>
            <Typography variant="body2">
              {quotaSummary.allocatedDevices} / {quotaSummary.totalDevices}
            </Typography>
          </Box>
          <Box>
            <LabelTitle>
              {polyglot.t('stoerk_id.subworkspaces.users_allocated')}
            </LabelTitle>
            <Typography variant="body2">
              {quotaSummary.allocatedUsers} / {quotaSummary.totalUsers}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Child workspaces table */}
      <ChildWorkspacesTable
        childData={childData}
        onEditQuota={(childId, users, devices) =>
          setEditQuota({ childId, users, devices })
        }
      />

      {/* Edit Quota Dialog */}
      <Dialog open={!!editQuota} onClose={() => setEditQuota(null)}>
        <DialogTitle>
          {polyglot.t('stoerk_id.subworkspaces.edit_quota_title')}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={polyglot.t('stoerk_id.subworkspaces.user_quota')}
            type="number"
            value={editQuota?.users ?? 0}
            onChange={(e) =>
              setEditQuota((prev) =>
                prev ? { ...prev, users: Number(e.target.value) } : null
              )
            }
            margin="normal"
            fullWidth
            disabled={isSavingQuota}
          />
          <TextField
            label={polyglot.t('stoerk_id.subworkspaces.device_quota')}
            type="number"
            value={editQuota?.devices ?? 0}
            onChange={(e) =>
              setEditQuota((prev) =>
                prev ? { ...prev, devices: Number(e.target.value) } : null
              )
            }
            margin="normal"
            fullWidth
            disabled={isSavingQuota}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditQuota(null)}
            disabled={isSavingQuota}
            variant="text"
          >
            {polyglot.t('button.cancel')}
          </Button>
          <LoadingButton
            onClick={handleSaveQuota}
            variant="contained"
            loading={isSavingQuota}
          >
            {polyglot.t('button.save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Internal table component for child workspaces */
function ChildWorkspacesTable({
  childData,
  onEditQuota,
}: {
  childData: {
    childId: string;
    workspace: WorkspaceAPI | undefined;
    usage: { commanders?: string[]; users?: string[] } | undefined;
    firstUser: string | undefined;
  }[];
  onEditQuota: (childId: string, users: number, devices: number) => void;
}) {
  const polyglot = getPolyglot();

  const columns: LightTableColumDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_name'),
        orderable: true,
      },
      {
        field: 'ownerEmail',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_owner_email'),
        orderable: true,
      },
      {
        field: 'status',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_status'),
        orderable: true,
      },
      {
        field: 'commanders',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_commanders'),
        orderable: true,
        TableCellProps: { align: 'right' },
      },
      {
        field: 'teamMembers',
        headerName: polyglot.t('stoerk_id.subworkspaces.col_team_members'),
        orderable: true,
        TableCellProps: { align: 'right' },
      },
      {
        field: 'actions',
        headerName: '',
        TableCellProps: { align: 'center', width: '56px' },
      },
    ],
    [polyglot]
  );

  const rows = useMemo(
    () =>
      childData.map(({ childId, workspace: ws, usage, firstUser }) => {
        const name = ws?.name ?? childId;
        const commandersUsed = usage?.commanders?.length ?? 0;
        const commandersAllocated = ws?.quota.devices ?? 0;
        const teamMembersUsed = usage?.users?.length ?? 0;
        const teamMembersAllocated = ws?.quota.users ?? 0;
        return {
          id: childId,
          name: {
            value: name,
            element: (
              <Link to={`/account/id/${childId}`} component={LinkRouter}>
                {name}
              </Link>
            ),
          },
          ownerEmail: firstUser ? <OwnerEmailCell userId={firstUser} /> : '-',
          status: {
            value: ws ? 'Active' : 'Unknown',
            element: (
              <LabelState variant={ws ? 'success' : 'info'}>
                {ws
                  ? polyglot.t('stoerk_id.subworkspaces.status_active')
                  : polyglot.t('stoerk_id.subworkspaces.status_unknown')}
              </LabelState>
            ),
          },
          commanders: {
            value: commandersUsed,
            element: `${commandersUsed} / ${commandersAllocated}`,
          },
          teamMembers: {
            value: teamMembersUsed,
            element: `${teamMembersUsed} / ${teamMembersAllocated}`,
          },
          actions: (
            <ActionsMenu
              onEditQuota={() =>
                onEditQuota(
                  childId,
                  ws?.quota.users ?? 0,
                  ws?.quota.devices ?? 0
                )
              }
            />
          ),
        };
      }),
    [childData, polyglot, onEditQuota]
  );

  return <LightTable columns={columns} rows={rows} variant="clear" />;
}

function ActionsMenu({ onEditQuota }: { onEditQuota: () => void }) {
  const polyglot = getPolyglot();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            handleClose();
            onEditQuota();
          }}
        >
          {polyglot.t('stoerk_id.subworkspaces.assign_quota')}
        </MenuItem>
      </Menu>
    </>
  );
}

function OwnerEmailCell({ userId }: { userId: string }) {
  const user = useAppSelector((state) => getUserSelector(userId)(state).data);
  return <>{user?.email ?? userId}</>;
}

export default SubWorkspacesOverview;
